// A small read cache: identical in-flight reads are de-duplicated and results are kept per branch head
// (spec/dashboard.md section 4.6, Q-N-6). No library: the pages need `useQuery` and an `invalidate` on a head move.
import { useCallback, useEffect, useRef, useState } from 'react';

interface Entry<T> {
  promise: Promise<T> | null;
  value?: T;
  error?: unknown;
  head: string | null | undefined;
}

export class QueryCache {
  private entries = new Map<string, Entry<unknown>>();
  private listeners = new Set<(key: string | null) => void>();

  subscribe(listener: (key: string | null) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Reads through the cache; a different `head` than the cached one refetches. */
  get<T>(key: string, fetcher: () => Promise<T>, head?: string | null): Promise<T> {
    const existing = this.entries.get(key) as Entry<T> | undefined;
    if (existing && existing.head === head) {
      if (existing.promise) return existing.promise;
      if (existing.error === undefined) return Promise.resolve(existing.value as T);
    }
    const entry: Entry<T> = { promise: null, head };
    entry.promise = fetcher().then(
      (value) => {
        entry.value = value;
        entry.promise = null;
        return value;
      },
      (error: unknown) => {
        entry.error = error;
        entry.promise = null;
        throw error;
      },
    );
    this.entries.set(key, entry as Entry<unknown>);
    return entry.promise;
  }

  peek<T>(key: string): T | undefined {
    return this.entries.get(key)?.value as T | undefined;
  }

  invalidate(prefix?: string): void {
    if (!prefix) this.entries.clear();
    else for (const k of Array.from(this.entries.keys())) if (k.startsWith(prefix)) this.entries.delete(k);
    for (const l of Array.from(this.listeners)) l(prefix ?? null);
  }

  /** Replaces a cached value (after a write that returned the entity). */
  set<T>(key: string, value: T, head?: string | null): void {
    this.entries.set(key, { promise: null, value, head });
    for (const l of Array.from(this.listeners)) l(key);
  }
}

export const queryCache = new QueryCache();

export interface QueryState<T> {
  data: T | undefined;
  error: unknown;
  loading: boolean;
  refetch: () => void;
}

/**
 * A cached read bound to a component. `key` null disables it. `head` (the branch head) is part of the identity, so a
 * head move refetches; `queryCache.invalidate(prefix)` refetches every mounted query under the prefix.
 */
export function useQuery<T>(key: string | null, fetcher: () => Promise<T>, opts: { head?: string | null; cache?: QueryCache } = {}): QueryState<T> {
  const cache = opts.cache ?? queryCache;
  const [state, setState] = useState<{ data: T | undefined; error: unknown; loading: boolean; key: string | null }>({ data: undefined, error: undefined, loading: key !== null, key });
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (key === null) {
      setState({ data: undefined, error: undefined, loading: false, key });
      return;
    }
    let cancelled = false;
    const peeked = cache.peek<T>(key);
    setState((s) => ({ data: peeked ?? (s.key === key ? s.data : undefined), error: undefined, loading: peeked === undefined, key }));
    cache.get(key, () => fetcherRef.current(), opts.head).then(
      (data) => {
        if (!cancelled) setState({ data, error: undefined, loading: false, key });
      },
      (error: unknown) => {
        if (!cancelled) setState((s) => ({ data: s.key === key ? s.data : undefined, error, loading: false, key }));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [key, opts.head, tick, cache]);

  useEffect(() => {
    if (key === null) return;
    return cache.subscribe((changed) => {
      if (changed === null || key.startsWith(changed) || changed === key) setTick((t) => t + 1);
    });
  }, [key, cache]);

  const refetch = useCallback(() => {
    if (key !== null) cache.invalidate(key);
  }, [key, cache]);

  return { data: state.key === key ? state.data : undefined, error: state.key === key ? state.error : undefined, loading: state.loading, refetch };
}
